import * as React from "react";
import { LexicalRichTextareaEditor } from "@/domains/forms";
import { validateEducation } from "../../../domains/profiles/utils/validation";

interface Education {
  id?: number;
  school_name?: string;
  qualification?: string;
  start_date?: string;
  end_date?: string;
  current?: boolean;
  description?: string;
  _destroy?: boolean;
}

interface EducationFormProps {
  educations: Education[];
  onEducationsChange: (educations: Education[]) => void;
  validationErrors?: { [index: number]: { [field: string]: string | null } };
  onValidationChange?: (index: number, field: string, error: string | null) => void;
  showValidationErrors?: boolean;
}

const BlankEducationItem: Education = {
  school_name: "",
  qualification: "",
  start_date: "",
  end_date: "",
  current: false,
  description: "",
};

export const EducationForm: React.FC<EducationFormProps> = ({
  educations,
  onEducationsChange,
  validationErrors = {},
  onValidationChange,
  showValidationErrors = true,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<number[]>([]);
  const newItemRef = React.useRef<HTMLDivElement>(null);

  // Auto-expand the first education when it gets added
  React.useEffect(() => {
    if (educations.length === 1 && !expandedIds.includes(0)) {
      setExpandedIds([0]);
    }
  }, [educations.length, expandedIds]);

  // Add custom styles for lexical editor integration
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .lexical-editor-wrapper.is-invalid .editor-inner {
        border-color: #dc3545;
      }
      .lexical-editor-wrapper.is-invalid .editor-inner:focus-within {
        border-color: #dc3545;
        box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
      }
      .lexical-editor-wrapper .editor-container {
        width: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const addEducationItem = () => {
    const updatedEducations = [...educations, BlankEducationItem];
    onEducationsChange(updatedEducations);
    const newIndex = updatedEducations.length - 1;
    setExpandedIds([...expandedIds, newIndex]);

    setTimeout(() => {
      newItemRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const removeEducationItem = (index: number) => {
    if (window.confirm("Are you sure you want to delete this education?")) {
      const education = educations[index];

      // If the education has an ID (existing record), mark for deletion
      if (education.id) {
        const updatedEducations = educations.map((edu, i) =>
          i === index ? { ...edu, _destroy: true } : edu
        );
        onEducationsChange(updatedEducations);
      } else {
        // If no ID (new record), remove from array entirely
        const updatedEducations = educations.filter((_, i) => i !== index);
        onEducationsChange(updatedEducations);

        // Update expanded state for remaining items
        setExpandedIds(prev =>
          prev.map(id => id > index ? id - 1 : id).filter(id => id !== index)
        );
      }
    }
  };

  const updateEducationItem = (index: number, updatedEducation: Education) => {
    const updatedEducations = educations.map((edu, i) =>
      i === index ? updatedEducation : edu
    );
    onEducationsChange(updatedEducations);
  };

  const validateField = (index: number, field: string, value: string) => {
    if (!onValidationChange) return;

    const testEducation = { ...educations[index], [field]: value };
    const errors = validateEducation(testEducation);
    onValidationChange(index, field, errors[field] || null);
  };

  const toggleAccordion = (index: number) => {
    setExpandedIds((prev) =>
      prev.includes(index) ? prev.filter((id) => id !== index) : [...prev, index]
    );
  };

  return (
    <div className="accordion modern-accordion" id="educationAccordion">
      {educations
        .map((education, originalIndex) => {
          // Skip items marked for deletion but preserve indexes for validation
          if (education._destroy) return null;

          return (
            <div
              className="accordion-item"
              key={originalIndex}
              ref={originalIndex === educations.length - 1 ? newItemRef : null}
            >
              <h2 className="accordion-header">
                {(() => {
                  const itemErrors = validationErrors[originalIndex];
                  const hasErrors = itemErrors && Object.values(itemErrors).some(e => e !== null);
                  const errorCount = itemErrors ? Object.values(itemErrors).filter(e => e !== null).length : 0;

                  return (
                    <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
                      <button
                        className={`accordion-button ${!expandedIds.includes(originalIndex) ? 'collapsed' : ''}`}
                        type="button"
                        onClick={() => toggleAccordion(originalIndex)}
                        style={{
                          cursor: 'pointer',
                          flex: 1,
                          paddingRight: '3rem',
                          backgroundColor: hasErrors ? '#fef2f2' : undefined,
                          borderLeft: hasErrors ? '4px solid #dc2626' : undefined,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {hasErrors && (
                            <i
                              className="fas fa-exclamation-circle"
                              style={{ color: '#dc2626', fontSize: '1rem' }}
                              title={`${errorCount} validation error${errorCount > 1 ? 's' : ''}`}
                              aria-hidden="true"
                            />
                          )}
                          <span>{education.qualification || 'New Qualification'} at {education.school_name || 'New School'}</span>
                          {hasErrors && (
                            <span style={{
                              backgroundColor: '#dc2626',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              marginLeft: '0.5rem'
                            }}>
                              {errorCount} error{errorCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeEducationItem(originalIndex)}
                        style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'inherit' }}
                        aria-label={`Delete education: ${education.qualification || 'New Qualification'}`}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  );
                })()}
              </h2>

              <div className={`accordion-collapse collapse ${expandedIds.includes(originalIndex) ? 'show' : ''}`}>
                <div className="accordion-body">
                  <EducationItem
                    education={education}
                    index={originalIndex}
                    onUpdate={(updatedEducation) => updateEducationItem(originalIndex, updatedEducation)}
                    onValidate={(field, value) => validateField(originalIndex, field, value)}
                    validationErrors={validationErrors[originalIndex] || {}}
                    showValidationErrors={showValidationErrors}
                  />
                </div>
              </div>
            </div>
          );
        })
        .filter(Boolean) // Remove null entries from deleted items
      }
      <div className="text-center mt-3">
        <button type="button" className="modern-btn btn-primary" onClick={addEducationItem}>
          <i className="fas fa-plus"></i>
          Add Education
        </button>
      </div>
    </div>
  );
};

interface EducationItemProps {
  education: Education;
  index: number;
  onUpdate: (education: Education) => void;
  onValidate: (field: string, value: string) => void;
  validationErrors: { [field: string]: string | null };
  showValidationErrors: boolean;
}

const EducationItem: React.FC<EducationItemProps> = ({
  education,
  index,
  onUpdate,
  onValidate,
  validationErrors,
  showValidationErrors,
}) => {
  const [educationState, setEducationState] = React.useState(education);

  // Update local state when education prop changes
  React.useEffect(() => {
    setEducationState(education);
  }, [education]);

  const handleFieldUpdate = (field: string, value: any) => {
    const updatedState = { ...educationState, [field]: value };
    setEducationState(updatedState);
    onUpdate(updatedState);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    handleFieldUpdate(name, type === 'checkbox' ? checked : value);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onValidate(name, value);
  };

  const getFieldClasses = (field: string) => {
    const hasError = showValidationErrors && validationErrors[field];
    if (field === 'description') {
      // For Lexical editor, return class name without 'form-control'
      return hasError ? 'is-invalid' : '';
    }
    return `form-control ${hasError ? 'is-invalid' : ''}`;
  };

  const renderValidationError = (field: string) => {
    if (!showValidationErrors || !validationErrors[field]) return null;

    return (
      <div className="invalid-feedback">
        {validationErrors[field]}
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`education_school_name_${index}`} className="required">
            School Name
          </label>
          <input
            type="text"
            className={getFieldClasses('school_name')}
            name="school_name"
            id={`education_school_name_${index}`}
            placeholder="e.g. University of California"
            value={educationState.school_name}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('school_name')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`education_qualification_${index}`} className="required">
            Qualification
          </label>
          <input
            type="text"
            className={getFieldClasses('qualification')}
            name="qualification"
            id={`education_qualification_${index}`}
            placeholder="e.g. Bachelor's Degree, Certificate"
            value={educationState.qualification}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('qualification')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`education_start_date_${index}`} className="required">
            Start Date
          </label>
          <input
            type="date"
            className={getFieldClasses('start_date')}
            name="start_date"
            id={`education_start_date_${index}`}
            value={educationState.start_date}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('start_date')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`education_end_date_${index}`} className={!educationState.current ? "required" : ""}>
            End Date
          </label>
          <input
            type="date"
            className={getFieldClasses('end_date')}
            name="end_date"
            id={`education_end_date_${index}`}
            value={educationState.end_date}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={educationState.current}
          />
          {renderValidationError('end_date')}
        </div>
      </div>

      <div className="col-12">
        <div className="modern-checkbox-group">
          <input
            type="checkbox"
            className="form-check-input"
            name="current"
            id={`education_current_${index}`}
            checked={educationState.current}
            onChange={handleInputChange}
          />
          <label className="form-check-label" htmlFor={`education_current_${index}`}>
            Currently attending
          </label>
        </div>
      </div>

      <div className="col-12">
        <div className="modern-form-group">
          <label htmlFor={`education_description_${index}`}>
            Description
          </label>
          <div className={`lexical-editor-wrapper ${getFieldClasses('description')}`}>
            <LexicalRichTextareaEditor
              key={`education-${education.id || index}-description`}
              editorState={educationState.description || ''}
              onChangeDescription={(value) => handleFieldUpdate('description', value)}
              id={`education_description_${index}`}
              placeholder="Describe relevant coursework, achievements, or projects..."
              minHeight="120px"
            />
          </div>
          {renderValidationError('description')}
        </div>
      </div>
    </div>
  );
};

export default EducationForm;